import { FilterState } from './types';
import { sanitizeFilters } from './filter';

/**
 * Shareable-URL encoding for the backlog filter state. The Table/Kanban/Timeline views share one
 * filter state (see use-filters); mirroring it into the query string — alongside the board + view
 * + task, which now live in the PATH (friendly routes) — makes a link reproduce what the sender
 * was looking at. Facets are comma-joined; the search is `q`.
 *
 * Only HUMAN-VALUE facets go in the URL (status/priority/type/source values are readable words).
 * The assignee facet is deliberately excluded: it holds member UUIDs, and the friendly-URL rule is
 * "no ugly ids in routes or filters". It still works in-app and persists via localStorage — it's
 * just not part of a shared link.
 */
export const FILTER_PARAM_KEYS = ['q', 'status', 'priority', 'type', 'source'] as const;

/** Query key for the item open in the details panel, encoded as its friendly KEY (e.g. `?item=SPB-3`)
 *  so a link reproduces the drawer-over-view the sender was looking at. Item ids never go in the URL —
 *  the human key does, matching the friendly routes. The Table/Kanban/Timeline views own this param
 *  (they hold the board's items to map the key ↔ id); see useItemUrlSync. */
export const ITEM_PARAM = 'item';

const splitList = (v: string | null): string[] =>
  v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [];

/** Encode a facet's values, prefixing each with `-` when the operator is "is not" (SparkQL convention),
 *  so a negated facet round-trips through a shared link. Empty → '' (the param is then deleted). */
const encodeFacet = (values: string[], op: 'is' | 'isNot' | undefined): string =>
  values.map((v) => (op === 'isNot' ? `-${v}` : v)).join(',');

/** Decode a facet param into its values + operator. A facet is "is not" when every value is `-`-prefixed
 *  (how {@link encodeFacet} writes it); the prefixes are stripped off the returned values. */
const decodeFacet = (raw: string | null): { values: string[]; isNot: boolean } => {
  const list = splitList(raw);
  const isNot = list.length > 0 && list.every((v) => v.startsWith('-'));
  return { values: list.map((v) => (v.startsWith('-') ? v.slice(1) : v)).filter(Boolean), isNot };
};

/** True when the query string carries any filter param, so it should win over storage. */
export const hasFilterParams = (p: URLSearchParams): boolean =>
  FILTER_PARAM_KEYS.some((k) => (p.get(k) ?? '') !== '');

/** Parse the shareable filter params into a (sanitized) FilterState, decoding each facet's is/is-not
 *  operator from a `-` value prefix. */
export const filtersFromParams = (p: URLSearchParams): FilterState => {
  const status = decodeFacet(p.get('status'));
  const priority = decodeFacet(p.get('priority'));
  const type = decodeFacet(p.get('type'));
  const source = decodeFacet(p.get('source'));
  const ops: Record<string, 'isNot'> = {};
  if (status.isNot) ops.statuses = 'isNot';
  if (priority.isNot) ops.priorities = 'isNot';
  if (type.isNot) ops.types = 'isNot';
  if (source.isNot) ops.sources = 'isNot';
  return sanitizeFilters({
    search: p.get('q') ?? '',
    statuses: status.values,
    priorities: priority.values,
    types: type.values,
    // assignees intentionally NOT read from the URL (member ids aren't put in the URL).
    sources: source.values,
    ops: Object.keys(ops).length ? ops : undefined,
  });
};

/** Write filter state onto `p` in place — setting non-empty facets, deleting empty ones —
 *  so the URL always mirrors exactly the active filters (a cleared filter leaves a clean URL). */
export const writeFilterParams = (p: URLSearchParams, f: FilterState): void => {
  const set = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k));
  set('q', f.search.trim());
  set('status', encodeFacet(f.statuses, f.ops?.statuses));
  set('priority', encodeFacet(f.priorities, f.ops?.priorities));
  set('type', encodeFacet(f.types, f.ops?.types));
  // assignee is intentionally NOT written (no member ids in the URL); a stale one is cleared.
  p.delete('assignee');
  set('source', encodeFacet(f.sources, f.ops?.sources));
};
